import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { sendLinearUpdate } from "~/features/configurator/Linear/api/sendLinearConfiguratorUpdate";
import { sendLinearDataReset } from "~/features/configurator/Linear/api/sendLinearStepReset";
import { sendLinearMiscChange, sendLinearStepChange } from "~/features/configurator/Linear/api/sendLinearStepChange";
import { sendLinearResetConfigurator } from "~/features/configurator/Linear/api/sendLinearResetConfigurator";
import { sendLinearAddToCart } from "~/features/configurator/Linear/api/sendLinearAddToCart";
import { SummaryData } from "~/features/configurator/Linear/v2/configuration/kuem/summary/KUEMUnitSummaryData";
import { type RootState } from "~/app/store";
import {
  type ConfiguratorPageDefinition,
  type StepHardcodedData,
} from "~/features/configurator/ConfiguratorPageDefinition";
import { KUEM_UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import * as UnitCstic from "~/features/configurator/Linear/v2/configuration/kuem/KUEMUnitCstics";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import type { LinearConfiguration, LinearSubstep } from "~/features/configurator/Linear/models";
import { validateFromDefinition } from "~/features/configurator/common/v2/validation";
import { findSelectedTileIndex } from "~/features/configurator/Linear/utils";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";
import { CsticNamingHelper } from "./CsticNamingHelper";
import { addFakeCstic } from "~/features/configurator/transformerHelper";
import { KUEMUnitSummaryParams } from "../../../components/KUEMUnitSummaryParams";
import { transformSummaryDataToV2, transformToSelectionData } from "~/features/configurator/Shaft/SummaryDataProvider";
import { getStep1SummaryData } from "./summary/KUEMUnitSummaryDataProvider";

const UNIT_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[0].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
];

export const KUEM_UNIT_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.System,
  productCode: KUEM_UNIT_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.unit.name",
  },
  apiCalls: {
    update: sendLinearUpdate,
    reset: sendLinearResetConfigurator,
    stepChange: sendLinearStepChange,
    stepReset: sendLinearDataReset,
    miscChange: sendLinearMiscChange,
    addToCart: sendLinearAddToCart,
  },
  summary: SummaryData,
  summaryParams: KUEMUnitSummaryParams,
  isAddToCartEnabled: (state: RootState) => {
    const kmats = state.linearConfigurator.kmats;

    const conflicts = state.linearConfigurator.conflicts;
    const validations = [0].map(
      (step) =>
        state.linearConfigurator.steps[step].substeps?.every(
          (substep) =>
            validateFromDefinition(state.linearConfigurator.substeps[substep] as LinearSubstep, kmats, conflicts)
              .substepValid,
        ) ?? false,
    );
    const addToCartEnabled = validations.every((validation) => validation);
    return addToCartEnabled;
  },
  isSaveEnabled: (state) => {
    return KUEM_UNIT_DEFINITION.isAddToCartEnabled(state);
  },
  canQueryAvailability: (state) => {
    return KUEM_UNIT_DEFINITION.isAddToCartEnabled(state);
  },
  canQueryPrice: (state) => {
    return KUEM_UNIT_DEFINITION.isAddToCartEnabled(state);
  },
  stepHardcodedData: UNIT_CONFIGURATOR_STEP_DATA,
  generateDesignation: (_, state) => {
    return state.designation;
  },
  gaConfiguratorName: "kuem-system",
  modelAndUpdatePreprocessor: (model) => {
    const mainKmat = getMainKmat({ kmats: model.kmats });

    addFakeCstic(mainKmat, {
      name: UnitCstic.PartSizing,
      value: "",
      values: [],
    });
    addFakeCstic(mainKmat, {
      name: UnitCstic.PartAmount,
      value: "1.0",
      values: [
        {
          value: "1.0",
          label: "1.0",
          key: "1.0",
          info: "",
          imageUrl: "",
        },
      ],
    });
    addFakeCstic(mainKmat, {
      name: UnitCstic.Closure,
      value: "WITHOUT",
      values: [
        {
          value: "WITHOUT",
          label: "WITHOUT",
          key: "WITHOUT",
          standard: true,
          info: "",
          imageUrl: "",
        },
      ],
    });

    const namingHelper = new CsticNamingHelper(mainKmat.cstics);

    namingHelper.updateNames(UnitCstic.GuidewayLength, {
      label: "configurator.unit.cstic.guidewayLength.name",
    });

    namingHelper.updateNames(UnitCstic.AL, {
      label: "configurator.unit.section.al.name",
    });

    namingHelper.updateNames(UnitCstic.AR, {
      label: "configurator.unit.section.ar.name",
    });

    namingHelper.updateNames(UnitCstic.PartSizing, {
      label: "configurator.unit.cstic.partSizing.name",
      value: {
        label: (value) => `configurator.unit.cstic.partSizing.${value}.name`,
      },
    });
    namingHelper.updateNames(UnitCstic.PartAmount, {
      label: "configurator.unit.cstic.partAmount.name",
      value: {
        label: (value) => `configurator.unit.cstic.partAmount.${value}.name`,
      },
    });
    namingHelper.updateNames(UnitCstic.Closure, {
      label: "configurator.unit.cstic.closure.name",
      value: {
        label: (value) => `configurator.unit.cstic.closure.${value}.name`,
      },
    });

    return model;
  },
  postDefinitionProcessor: (model) => {
    model.allowedSteps = calculateAllowedSteps(model);
    return model;
  },
} as const;

export type NamingProps = {
  label?: string;
  description?: string;
  info?: string;
  value?: {
    label?: (value: string) => string;
    imageUrl?: (value: string) => string;
  };
};
function pageIsConfigured(model: LinearConfiguration, page: number): boolean {
  return (model.steps[page]!.substeps ?? []).every((substepType) => {
    const substep = model.substeps[substepType];

    substep.selectedTile = findSelectedTileIndex(substep as LinearSubstep, model.kmats);

    return validateFromDefinition(
      substep as LinearSubstep,
      model.kmats,
      (model as LinearConfiguration).conflictedCstics ?? [],
    ).substepValid;
  });
}

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const canVisitStep2 = pageIsConfigured(model, 0);

  const allowedSteps = [
    [true, canVisitStep2],
    [true, true],
  ];

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(KUEM_UNIT_DEFINITION);
